import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, CircleHelp, Crown, Layers3, Link2, Users } from 'lucide-react';
import { authservice } from '../../../../components/api/authservice';
import { roleService } from '../../../../components/api/roleService';
import { listIdentityProviders } from './identityProviders/entraConnectionService';
import { useAccessTheme } from './useAccessTheme';
import { logAccess } from './accessSettingsLogger';
import {
  isWorkspaceAdmin,
  mapDirectoryUser,
  userStatus,
  type DirectoryUser,
} from './accessDirectoryUtils';

interface AccessIdentityOverviewProps {
  tabs?: React.ReactNode;
  onManageUsers: () => void;
  onViewAdmins: () => void;
  onManageRoles: () => void;
  onSetupSso: () => void;
}

interface OverviewStats {
  activeUsers: number | null;
  admins: number | null;
  roles: number | null;
  providers: number | null;
}

const AccessIdentityOverview: React.FC<AccessIdentityOverviewProps> = ({
  tabs,
  onManageUsers,
  onViewAdmins,
  onManageRoles,
  onSetupSso,
}) => {
  const t = useAccessTheme();
  const [helpOpen, setHelpOpen] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState<OverviewStats>({
    activeUsers: null,
    admins: null,
    roles: null,
    providers: null,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const next: OverviewStats = { activeUsers: null, admins: null, roles: null, providers: null };
      try {
        const usersRaw = await authservice.getUsers();
        const users = (Array.isArray(usersRaw) ? usersRaw : []).map((row) => mapDirectoryUser(row as DirectoryUser));
        next.activeUsers = users.filter((user) => userStatus(user) === 'active').length;
        next.admins = users.filter(isWorkspaceAdmin).length;
      } catch (err) {
        logAccess('error', 'overview_users_load_failed', {
          message: err instanceof Error ? err.message : String(err),
        });
      }
      try {
        const roles = await roleService.getRoleDefinitions();
        next.roles = (roles || []).length;
      } catch (err) {
        logAccess('error', 'overview_roles_load_failed', {
          message: err instanceof Error ? err.message : String(err),
        });
      }
      try {
        const providers = await listIdentityProviders();
        next.providers = Array.isArray(providers) ? providers.length : 0;
      } catch (err) {
        logAccess('error', 'overview_providers_load_failed', {
          message: err instanceof Error ? err.message : String(err),
        });
      }
      if (!cancelled) {
        setStats(next);
        logAccess('app', 'overview_loaded', { ...next } as Record<string, unknown>);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!helpOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(event.target as Node)) setHelpOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [helpOpen]);

  const metric = (value: number | null) => (value == null ? '—' : String(value));

  const cardSurface = t.isLight
    ? 'bg-white border-neutral-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-indigo-200 hover:shadow-[0_6px_16px_rgba(79,70,229,0.08)]'
    : 'bg-neutral-900/50 border-neutral-800 hover:border-indigo-700/40';

  const cards = [
    {
      key: 'users',
      label: 'Active users',
      value: metric(stats.activeUsers),
      caption: null as string | null,
      onClick: onManageUsers,
      Icon: Users,
      iconWrap: t.isLight ? 'bg-indigo-50 text-indigo-500' : 'bg-indigo-900/40 text-indigo-300',
    },
    {
      key: 'admins',
      label: 'Workspace admins',
      value: metric(stats.admins),
      caption: null,
      onClick: onViewAdmins,
      Icon: Crown,
      iconWrap: t.isLight ? 'bg-violet-50 text-violet-500' : 'bg-violet-900/40 text-violet-300',
    },
    {
      key: 'roles',
      label: 'Defined roles',
      value: metric(stats.roles),
      caption: null,
      onClick: onManageRoles,
      Icon: Layers3,
      iconWrap: t.isLight ? 'bg-emerald-50 text-emerald-600' : 'bg-emerald-900/40 text-emerald-300',
    },
    {
      key: 'identity',
      label: 'Identity providers',
      value: stats.providers == null ? '—' : stats.providers === 0 ? 'Not configured' : String(stats.providers),
      caption: 'Directory SSO',
      onClick: onSetupSso,
      Icon: Link2,
      iconWrap: t.isLight ? 'bg-orange-50 text-orange-500' : 'bg-orange-900/40 text-orange-300',
    },
  ];

  return (
    <div className="space-y-5">
      <div
        className={`-mx-6 -mt-5 px-6 pt-6 pb-5 ${
          t.isLight ? 'bg-white border-neutral-200/80' : 'bg-neutral-900 border-neutral-800'
        } border-b`}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-[28px] font-semibold tracking-tight leading-none ${t.title}`}>
              Access & Identity
            </h2>
            <p className={`text-[15px] mt-2 leading-relaxed max-w-2xl ${t.muted}`}>
              Manage people, roles, authentication, and directory integrations for your organization.
            </p>
          </div>
          <div className="relative shrink-0" ref={helpRef}>
            <button
              type="button"
              onClick={() => setHelpOpen((open) => !open)}
              className={`inline-flex items-center gap-1.5 text-sm font-medium ${t.accentText} hover:underline`}
              aria-label="Help and feedback"
            >
              <CircleHelp className="w-4 h-4" />
              Help & feedback
            </button>
            {helpOpen ? (
              <div className={`absolute right-0 z-20 mt-2 w-72 rounded-xl border p-3.5 text-sm shadow-lg ${t.panel}`}>
                <p className={`font-medium mb-1 ${t.title}`}>What you can manage</p>
                <p className={`leading-relaxed ${t.muted}`}>
                  Users are people in this workspace. Roles define what they can access. Identity providers connect
                  directory SSO such as Microsoft Entra ID.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {tabs}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
        {cards.map((card) => {
          const Icon = card.Icon;
          return (
            <button
              key={card.key}
              type="button"
              onClick={card.onClick}
              className={`flex items-center gap-2 text-left rounded-xl border px-3 py-2 transition-all duration-200 ${cardSurface}`}
            >
              <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center shrink-0 ${card.iconWrap}`}>
                <Icon className="w-3.5 h-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className={`text-[11px] font-medium leading-none ${t.muted}`}>{card.label}</p>
                <p className={`text-[15px] font-semibold tracking-tight leading-tight mt-0.5 ${t.title}`}>{card.value}</p>
                {card.caption ? <p className={`text-[11px] leading-none mt-0.5 ${t.muted}`}>{card.caption}</p> : null}
              </div>
              <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${t.muted}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AccessIdentityOverview;
